import React, { PureComponent, forwardRef } from 'react';
import BaseTable_, {
  Column,
  callOrReturn,
  normalizeColumns,
} from 'react-base-table';
import { Checkbox } from '../checkbox';
import _ from 'lodash';
import { isMobile } from 'react-device-detect';
import { useDirection } from '../custom-hooks/useDirection';
export const BaseTable = forwardRef(({ children, ...props }, ref) => {
  const direction = useDirection();
  return <BaseTable_ direction={direction} {...props}>{children}</BaseTable_>;
});

export class SelectionCell extends PureComponent {
  _handleChange = (e) => {
    const { rowData, rowIndex, column } = this.props;
    const { onChange } = column;

    onChange({ selected: e.target.checked, rowData, rowIndex });
  };

  render() {
    const { rowData, column } = this.props;
    const { selectedRowKeys, rowKey, hovered } = column;
    const checked = selectedRowKeys?.includes?.(rowData[rowKey]);
    if (checked) console.warn(checked);
    return (
      <Checkbox
        pt={1}
        display={checked || hovered?.id === rowData?.id || isMobile ? 'block' : 'none'}
        type="checkbox"
        isChecked={checked}
        onChange={this._handleChange}
        borderColor="blackAlpha.500"
      />
    );
  }
}

export class SelectableTable extends PureComponent {
  constructor(props) {
    super(props);

    const {
      selectedRowKeys,
      defaultSelectedRowKeys,
      expandedRowKeys,
      defaultExpandedRowKeys,
    } = props;
    this.state = {
      selectedRowKeys:
        (selectedRowKeys !== undefined
          ? selectedRowKeys
          : defaultSelectedRowKeys) || [],
      expandedRowKeys:
        (expandedRowKeys !== undefined
          ? expandedRowKeys
          : defaultExpandedRowKeys) || [],
    };
  }
  
  componentDidUpdate(prevProps) {
    const prevIds = (prevProps.data || []).map(
      (r) => r[this.props.rowKey]
    );
    const currentIds = (this.props.data || []).map(
      (r) => r[this.props.rowKey]
    );
    const hasChanged = prevIds.join(",") !== currentIds.join(",");

    if (hasChanged) {
      this.setSelectedRowKeys([]);
      this.props.onSelectedRowsChange?.([]);
    }
  }


  /**
   * Set `selectedRowKeys` manually.
   * This method is available only if `selectedRowKeys` is uncontrolled.
   *
   * @param {array} selectedRowKeys
   */
  setSelectedRowKeys(selectedRowKeys) {
    // if `selectedRowKeys` is controlled
    if (this.props.selectedRowKeys !== undefined) return;

    this.setState({
      selectedRowKeys: _.cloneDeep(selectedRowKeys),
    });
  }

  /**
   * See BaseTable#setExpandedRowKeys
   */
  setExpandedRowKeys(expandedRowKeys) {
    // if `expandedRowKeys` is controlled
    if (this.props.expandedRowKeys !== undefined) return;

    this.setState({
      expandedRowKeys: _.cloneDeep(expandedRowKeys),
    });
  }

  /* some other custom methods and proxy methods */

  /**
   * Remove rowKeys from inner state  manually, it's useful to purge dirty state after rows removed.
   * This method is available only if `selectedRowKeys` or `expandedRowKeys` is uncontrolled.
   *
   * @param {array} rowKeys
   */
  removeRowKeysFromState(rowKeys) {
    if (!Array.isArray(rowKeys)) return;

    const state = {};
    if (
      this.props.selectedRowKeys === undefined &&
      this.state.selectedRowKeys.length > 0
    ) {
      state.selectedRowKeys = this.state.selectedRowKeys.filter(
        (key) => !rowKeys.includes(key)
      );
    }
    if (
      this.props.expandedRowKeys === undefined &&
      this.state.expandedRowKeys.length > 0
    ) {
      state.expandedRowKeys = this.state.expandedRowKeys.filter(
        (key) => !rowKeys.includes(key)
      );
    }
    if (state.selectedRowKeys || state.expandedRowKeys) {
      this.setState(state);
    }
  }

  _handleSelectChange = ({ selected, rowData, rowIndex }) => {
    const selectedRowKeys = [...this.state.selectedRowKeys];
    const key = rowData[this.props.rowKey];

    if (selected) {
      if (!selectedRowKeys.includes(key)) selectedRowKeys.push(key);
    } else {
      const index = selectedRowKeys.indexOf(key);
      if (index > -1) {
        selectedRowKeys.splice(index, 1);
      }
    }

    // if `selectedRowKeys` is uncontrolled, update internal state
    if (this.props.selectedRowKeys === undefined) {
      this.setState({ selectedRowKeys });
    }
    this.props.onRowSelect?.({ selected, rowData, rowIndex });
    this.props.onSelectedRowsChange?.(selectedRowKeys);
  };

  _rowClassName = ({ rowData, rowIndex }) => {
    const { rowClassName, rowKey } = this.props;
    const { selectedRowKeys } = this.state;

    const rowClass = rowClassName
      ? callOrReturn(rowClassName, { rowData, rowIndex })
      : '';
    const key = rowData[rowKey];

    return [rowClass, selectedRowKeys?.includes(key) && 'row-selected']
      .filter(Boolean)
      .concat(' ');
  };

  render() {
    const { columns, children, selectable, selectionColumnProps, ...rest } =
      this.props;
    const { selectedRowKeys } = this.state;

    // you'd better memoize this operation
    let _columns = columns || normalizeColumns(children);
    
    // Center-align the 'shared_with' column (Who can access)
    _columns = _columns.map((column) => {
      if (column.key === 'shared_with') {
        const originalCellRenderer = column.cellRenderer;
        
        return {
          ...column,
          cellRenderer: originalCellRenderer
            ? (props) => {
                const result = originalCellRenderer(props);
                // Wrap the cell content in a centered container
                if (result && typeof result === 'object') {
                  return (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: '100%',
                        textAlign: 'center',
                      }}
                    >
                      {result}
                    </div>
                  );
                }
                return result;
              }
            : undefined,
        };
      }
      return column;
    });
    
    if (selectable) {
      const selectionColumn = {
        width: 40,
        flexShrink: 0,
        resizable: false,
        frozen: Column.FrozenDirection.LEFT,
        cellRenderer: SelectionCell,
        headerRenderer: ({ column }) => {
          const { data, rowKey } = this.props;
          const allSelected = selectedRowKeys?.length === data?.length;
          const indeterminate =
            selectedRowKeys?.length > 0 && selectedRowKeys?.length < data?.length;
          return (
            <Checkbox
              isChecked={allSelected}
              isIndeterminate={indeterminate}
              onChange={(e) => {
                if (e.target.checked) {
                  const allKeys = data?.map((row) => row[rowKey]);
                  this.setSelectedRowKeys(allKeys);
                  this.props.onSelectedRowsChange?.(allKeys);
                } else {
                  this.setSelectedRowKeys([]);
                  this.props.onSelectedRowsChange?.([]);
                }
              }}
            />
          );
        },
        ...selectionColumnProps,
        key: '__selection__',
        rowKey: this.props.rowKey,
        hovered: this.props.hovered,
        selectedRowKeys: selectedRowKeys,
        onChange: this._handleSelectChange,
      };
      _columns = [selectionColumn, ..._columns];
    }

    return (
      <BaseTable
        {...rest}
        columns={_columns}
        rowClassName={this._rowClassName}
      />
    );
  }
}
